import type { CaseStatus, VolumeOut } from "@/api";
import { facadeEditability } from "@/lib/facadeEditability";
import { formatDay, formatMoney } from "@/lib/format";

import styles from "./VolumeFigure.module.css";

function gapWords(count: number): string {
  return count === 1 ? "1 Lücke" : `${count} Lücken`;
}

interface VolumeFigureProps {
  volume: VolumeOut | null;
  /** `row`: a table cell, right-aligned. `sheet`: the fact sheet's big figure with its origin. */
  size?: "row" | "sheet";
  /**
   * How a row says there is no amount. `dash` (default): under a column head
   * that already says what the column is. `words`: where no head does — the
   * start page's list, a handover card — a bare „—" says nothing there
   * (round-2 screen check, R2-12). `both`: the two, marked `data-missing`,
   * the words hidden — for a list whose head goes at some width (the
   * register's cards), which then swaps them in its own tier (UX gate round
   * 2, #1).
   */
  missing?: "dash" | "words" | "both";
  /**
   * A row's gap count under the amount. `false` where the row names the gaps
   * beside its stage already (`gapsInWords`, UX gate round 4, #2) — „mit 1
   * Lücke" twice in one row. The fact sheet always has its origin line.
   */
  gaps?: boolean;
  /**
   * The Vorgang's status, where the caller has it: a cancelled Vorgang (or
   * one past „Angebot raus“) takes no new calculation, so a stale figure
   * there gets no „neu rechnen" (review Task 12, #4).
   */
  status?: CaseStatus | null;
}

/**
 * An amount whose calculation belongs to an older Stand than the Vorgang's
 * newest (`volume.stale`, UX-Gutachten 23.10.2026, Aufgabe 1): „658,70 €"
 * beside „In Planung" read as a valid price. The calculation screen's own
 * word for it („Stand veraltet — neu rechnen.").
 */
export const STALE_WORD = "Stand veraltet";

/** The whole reason, on the figure's `title`. */
export const STALE_TITLE =
  "Die Kalkulation gehört zu einem älteren Stand — sie zählt nicht in die Summe. Neu rechnen in der Kalkulation.";

/** The same where the Vorgang takes no calculation any more (cancelled). */
const STALE_TITLE_CLOSED = "Die Kalkulation gehört zu einem älteren Stand — sie zählt nicht in die Summe.";

/** The fact sheet's line: room for the way on. */
const STALE_SHEET = "Stand veraltet — neu rechnen in der Kalkulation.";

/** And without a way on (review Task 12, #4): a cancelled Vorgang is rewritten nowhere. */
const STALE_SHEET_CLOSED = "Stand veraltet.";

/** Short enough for a row's amount column; the whole sentence stays on `title`. */
const SHORT: Record<string, string> = { "noch nicht kalkuliert": "nicht kalkuliert" };

/**
 * No amount, said quietly (UX gate round 1, #12): in a row a dash in the
 * secondary grey, the words on `title` and for a screen reader — „noch nicht
 * kalkuliert" in italic 15 px weighed more than the stage chip beside it and
 * cut titles on a phone for a non-information. The register's own dash
 * (`RegisterRow`) is the same sign. Without a column head to explain it, the
 * dash becomes the words themselves, small and upright in --ink-3 (R2-12).
 */
function NoAmount({ words, missing }: { words: string; missing: "dash" | "words" | "both" }) {
  const wordForm = (
    <span className={styles.words} title={words} data-missing="words">
      {SHORT[words] ?? words}
    </span>
  );
  const dashForm = (
    <span className={styles.dash} title={words} data-missing="dash">
      <span aria-hidden="true">—</span>
      <span className="visually-hidden">{words}</span>
    </span>
  );
  if (missing === "words") return wordForm;
  if (missing === "dash") return dashForm;
  return (
    <span className={styles.both}>
      {dashForm}
      {wordForm}
    </span>
  );
}

/**
 * The project volume (design §3): `vkTotal` of the latest calculation, net.
 * Never estimated (E-51) and never added up here (the register's sum is the
 * server's `totalVk`). A Vorgang without a calculation says so — „0,00 €"
 * would read as a price, and a wrong price is worse than none. In a row it
 * says so with a dash (`NoAmount`); on the fact sheet, where the figure
 * stands alone, in words.
 *
 * **One drawing for an amount of an older Stand** (`volume.stale`), wherever
 * a `VolumeOut` is shown — register, start page, fact sheet, handover card
 * (Task 12's two copies moved here, 10f): the amount stays — a number that
 * vanished would hide what was offered last —, dimmed, with „Stand
 * veraltet" in words under it, not a colour alone, where the gap count of a
 * calculation that no longer holds would have stood; the reason on `title`.
 * No red: stale is no error, only no valid price.
 */
export function VolumeFigure({ volume, size = "row", missing = "dash", gaps = true, status = null }: VolumeFigureProps) {
  const sheet = size === "sheet";
  const editability = status !== null ? facadeEditability(status) : "open";
  const closed = editability === "cancelled" || editability === "sent";
  if (volume === null) {
    return sheet ? (
      <span className={styles.none}>noch nicht kalkuliert</span>
    ) : (
      <NoAmount words="noch nicht kalkuliert" missing={missing} />
    );
  }
  const stale = volume.stale;
  const origin = [
    `Kalkulation ${volume.calcNo} · ${formatDay(volume.calculatedAt)}`,
    volume.gapCount > 0 && !stale ? `mit ${gapWords(volume.gapCount)}` : null,
  ]
    .filter((part) => part !== null)
    .join(", ");
  return (
    <span
      className={`${styles.figure} ${sheet ? styles.sheet : ""}`}
      title={stale ? (closed ? STALE_TITLE_CLOSED : STALE_TITLE) : undefined}
      data-stale={stale ? "" : undefined}
    >
      {volume.vkTotal !== null ? (
        <span className={`num ${styles.amount} ${stale ? styles.staleAmount : ""}`}>
          {formatMoney(volume.vkTotal)}
        </span>
      ) : sheet ? (
        <span className={styles.none}>ohne Summe</span>
      ) : (
        <NoAmount words="ohne Summe" missing={missing} />
      )}
      {sheet ? (
        <>
          <span className={styles.sub}>{origin}</span>
          {stale ? <span className={styles.staleWord}>{closed ? STALE_SHEET_CLOSED : STALE_SHEET}</span> : null}
        </>
      ) : stale ? (
        <span className={styles.staleWord}>{STALE_WORD}</span>
      ) : gaps && volume.gapCount > 0 ? (
        <span className={styles.sub} data-volume-sub="">
          mit {gapWords(volume.gapCount)}
        </span>
      ) : null}
    </span>
  );
}
